<template>
    <div class="flex gap-4 flex-col">
        <div v-if="props.title" class="text-xl font-extrabold">Custom Headers</div>

        <auth-config-form-header-row v-for="(_, index) in data.headers"
            :key="index"
            :index="index"
            :headers="data.headers"
            :authConfigId="data.auth_config_id"
            :removable="data.headers.length > 1"
            :autofillLock="autofillLock"
            v-model="data.headers[index]"
            @focus="autofillLock = false"
            @remove="removeRow(index)"></auth-config-form-header-row>

        <div class="flex gap-2">
            <vx-button type="button"
                data-test="add-header"
                color="muted"
                :disabled="data.headers.length >= MAX_HEADERS"
                @click="addRow()">
                <font-awesome-icon widthAuto :icon="faPlus"></font-awesome-icon>
                Add header
            </vx-button>

            <vx-button @click="basicModal = true"
                data-test="basic-auth-helper"
                type="button"
                color="muted">
                Basic Auth Helper
                <font-awesome-icon widthAuto :icon="faHammer"></font-awesome-icon>
            </vx-button>
        </div>

        <vx-modal v-model:visible="basicModal"
            size="md"
            padding="standard">
            <template v-slot:header>Basic Auth Helper</template>
            <template v-slot:subheader>Give us the auth and we'll add a header row.</template>

            <div class="flex flex-col gap-4">
                <div>
                    <vx-label size="lg">Username</vx-label>
                    <vx-input autocomplete="off"
                        data-test="basic-auth-username"
                        size="lg"
                        v-model="basic.username"
                        :error="displayFirstError(basicValidation.username)"
                        @keypress.enter="makeHeaders()"></vx-input>
                </div>

                <div>
                    <vx-label size="lg">Password</vx-label>
                    <vx-input size="lg"
                        autocomplete="off"
                        data-test="basic-auth-password"
                        :type="basicShowSecret ? 'text' : 'password'"
                        :error="displayFirstError(basicValidation.password)"
                        v-model="basic.password"
                        @keypress.enter="makeHeaders()">
                        <template v-slot:right>
                            <vx-button class="-mr-1"
                                @click.prevent="basicShowSecret = !basicShowSecret"
                                color="transparent"
                                size="sm"
                                :aria-label="basicShowSecret ? 'Hide password' : 'Show password'">
                                <font-awesome-icon widthAuto :icon="basicShowSecret ? faEyeSlash : faEye"></font-awesome-icon>
                            </vx-button>
                        </template>
                    </vx-input>
                </div>
            </div>

            <template v-slot:footer>
                <div class="flex flex-col-reverse gap-4 lg:flex-row">
                    <vx-button @click="basicModal = false"
                        type="button"
                        class="grow lg:w-20 lg:grow-0"
                        size="lg"
                        color="muted">
                        Cancel
                    </vx-button>
                    <vx-button type="button"
                        data-test="make-header"
                        @click="makeHeaders()"
                        class="grow"
                        size="lg"
                        color="primary">
                        Add header <font-awesome-icon widthAuto :icon="faHammer"></font-awesome-icon>
                    </vx-button>
                </div>
            </template>
        </vx-modal>
    </div>
</template>

<script setup>
import { faEye, faEyeSlash, faHammer, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxInput,
    VxLabel,
    VxModal,
} from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { computed, reactive, ref } from 'vue';
import AuthConfigFormHeaderRow from '~/components/settings/integrations/auth-configs/form/AuthConfigFormHeaderRow.vue';
import { useAuthConfigForm } from '~/components/settings/integrations/auth-configs/form/useAuthConfigForm';
import { displayFirstError } from '~/utils/validation';

const MAX_HEADERS = 5;

const props = defineProps({
    title: {
        type: Boolean,
        default: false,
    },
});

const { data } = useAuthConfigForm();

const autofillLock = ref(true);

const basicModal = ref(false);
const basicShowSecret = ref(false);
const basic = reactive({
    username: '',
    password: '',
});

const addRow = () => {
    if (data.value.headers.length >= MAX_HEADERS) {return;}
    data.value.headers.push({ name: '', value: '' });
};

const removeRow = (index) => {
    if (data.value.headers.length <= 1) {return;}
    data.value.headers.splice(index, 1);
};

const makeHeaders = () => {
    if (basic.username.trim() === '' || basic.password.trim() === '') {
        basicValidation.value.$touch();
        return;
    }

    const encoded = btoa(`${basic.username}:${basic.password}`);
    data.value.headers = [{ name: 'authorization', value: `Basic ${encoded}` }];

    basic.username = '';
    basic.password = '';
    basicShowSecret.value = false;
    basicModal.value = false;
};

const basicValidation = useVuelidate(
    computed(() => ({
        username: {
            requiredIf: requiredIf(() => basicModal.value),
        },
        password: {
            requiredIf: requiredIf(() => basicModal.value),
        },
    })),
    basic,
);

useVuelidate();

defineExpose({
    basicModal,
});
</script>
